import React from 'react';
import styled from 'styled-components';
import {
  BeneficiaryType,
  LeadSigningDetailsDto,
  ParentDto,
  SpouseDto,
  SubmitterDto,
  SubmitterSourceType,
  emptyParent,
  emptySpouse,
  emptySubmitter,
} from '../services/leadSigningService';
import { validateIdField, validateIsraeliId } from '../utils/validationUtils';

interface Props {
  value: LeadSigningDetailsDto;
  errors: Record<string, string>;
  disabled?: boolean;
  // הסתרת בלוק "פרטי לקוח" — מיועד למסכי לקוח שמציגים את הנתונים האלו במקום אחר.
  hideClientSection?: boolean;
  onChange: (next: LeadSigningDetailsDto) => void;
  onErrorsChange: (errors: Record<string, string>) => void;
}

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const RequiredLabel = styled(Label)`
  &::after {
    content: ' *';
    color: var(--error-color);
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${p => p.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: ${p => p.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${p => p.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }

  &:disabled, &:read-only {
    background: var(--background-color);
    color: var(--text-secondary);
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--error-color);
  margin-top: 4px;
`;

const SuccessText = styled.span`
  font-size: 12px;
  color: var(--success-color);
  margin-top: 4px;
`;

const SectionHeader = styled.div`
  background: var(--primary-light);
  padding: 12px 16px;
  border-radius: var(--border-radius-sm);
  margin: 20px 0 12px 0;
  border-right: 3px solid var(--primary-color);
  font-weight: 600;
  color: var(--primary-color);
`;

const ToggleGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;
`;

const ToggleButton = styled.button<{ $active?: boolean; disabled?: boolean }>`
  padding: 10px 12px;
  border: 1.5px solid ${p => p.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  background: ${p => p.$active ? 'var(--primary-light)' : 'white'};
  color: ${p => p.$active ? 'var(--primary-color)' : 'var(--text-primary)'};
  font-weight: ${p => p.$active ? 600 : 500};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  transition: all 0.15s ease;
  opacity: ${p => p.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    border-color: var(--primary-color);
    background: var(--primary-light);
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const Chip = styled.button<{ $active?: boolean; disabled?: boolean }>`
  padding: 6px 14px;
  border: 1px solid ${p => p.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: 999px;
  background: ${p => p.$active ? 'var(--primary-color)' : 'white'};
  color: ${p => p.$active ? 'white' : 'var(--text-primary)'};
  font-size: 13px;
  font-weight: 500;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.15s ease;
  opacity: ${p => p.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    border-color: var(--primary-color);
    background: ${p => p.$active ? 'var(--primary-hover)' : 'var(--primary-light)'};
  }
`;

const IdFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InputWithCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: var(--primary-color);
`;

const RELATIONSHIP_OPTIONS = ['בן/בת זוג', 'בן', 'בת', 'בעל', 'אישה', 'אב', 'אם', 'אח', 'אחות', 'אחר'];

// מיפוי מקור מגיש → ערך ברירת מחדל ל"קרבת משפחה"
const relationshipFromSource = (s: SubmitterSourceType): string | undefined => {
  switch (s) {
    case 'father': return 'אב';
    case 'mother': return 'אם';
    case 'spouse': return 'בן/בת זוג';
    case 'other': return undefined; // נשמר ערך נוכחי, לא נדרס
  }
};

const LeadSigningDetails: React.FC<Props> = ({ value, errors, disabled, hideClientSection, onChange, onErrorsChange }) => {
  const clearError = (key: string) => {
    if (errors[key]) {
      const next = { ...errors };
      delete next[key];
      onErrorsChange(next);
    }
  };

  const setError = (key: string, message: string | null) => {
    const next = { ...errors };
    if (message) {
      next[key] = message;
    } else {
      delete next[key];
    }
    onErrorsChange(next);
  };

  // ============================================================
  // Toggle change — אפס את הסעיפים הלא רלוונטיים, ואפס את ה-Submitter
  // ============================================================
  const handleBeneficiaryTypeChange = (next: BeneficiaryType) => {
    if (next === value.beneficiaryType) return;

    let father = value.father;
    let mother = value.mother;
    let spouse = value.spouse;

    if (next === 'adult') {
      father = emptyParent();
      mother = emptyParent();
    } else if (next === 'child') {
      spouse = emptySpouse();
    } else {
      father = emptyParent();
      mother = emptyParent();
      spouse = emptySpouse();
    }

    onChange({
      ...value,
      beneficiaryType: next,
      father,
      mother,
      spouse,
      submitter: emptySubmitter(),
    });

    // ניקוי שגיאות של הסעיפים שאופסו
    const remainingErrors: Record<string, string> = {};
    Object.entries(errors).forEach(([k, v]) => {
      if (next === 'adult' && (k.startsWith('father.') || k.startsWith('mother.'))) return;
      if (next === 'child' && k.startsWith('spouse.')) return;
      if (next === 'other' && (k.startsWith('father.') || k.startsWith('mother.') || k.startsWith('spouse.'))) return;
      if (k.startsWith('submitter.')) return;
      remainingErrors[k] = v;
    });
    onErrorsChange(remainingErrors);
  };

  // ============================================================
  // עדכון מגיש הבקשה לפי מקור
  // ============================================================
  const handleSubmitterSourceChange = (sourceType: SubmitterSourceType) => {
    if (sourceType === value.submitter.sourceType) return;

    // אם המקור אינו "אחר" — נמלא גם את שדה הקרבה אוטומטית
    const autoRelationship = relationshipFromSource(sourceType);

    let nextSubmitter: SubmitterDto = {
      sourceType,
      relationship: autoRelationship ?? value.submitter.relationship,
    };

    if (sourceType === 'father') {
      nextSubmitter = {
        ...nextSubmitter,
        name: value.father.name,
        idNumber: value.father.idNumber,
        address: value.father.address,
        city: value.father.city,
      };
    } else if (sourceType === 'mother') {
      nextSubmitter = {
        ...nextSubmitter,
        name: value.mother.name,
        idNumber: value.mother.idNumber,
        address: value.mother.address,
        city: value.mother.city,
      };
    } else if (sourceType === 'spouse') {
      nextSubmitter = {
        ...nextSubmitter,
        name: value.spouse.name,
        idNumber: value.spouse.idNumber,
        address: value.spouse.address,
        city: value.spouse.city,
      };
    }

    onChange({ ...value, submitter: nextSubmitter });

    // ניקוי שגיאות מגיש כשמשנים מקור — הערכים שכובים אוטומטית
    const cleared = { ...errors };
    Object.keys(cleared).forEach(k => { if (k.startsWith('submitter.')) delete cleared[k]; });
    onErrorsChange(cleared);
  };

  // ============================================================
  // אפשרויות מגיש לפי הטוגל
  // ============================================================
  const submitterOptions: SubmitterSourceType[] = (() => {
    switch (value.beneficiaryType) {
      case 'child': return ['father', 'mother', 'other'];
      case 'adult': return ['spouse', 'other'];
      case 'other': return ['other'];
    }
  })();

  const submitterSourceLabel = (s: SubmitterSourceType): string => {
    switch (s) {
      case 'father': return 'אבא';
      case 'mother': return 'אמא';
      case 'spouse': return 'בן/בת זוג';
      case 'other': return 'אחר';
    }
  };

  const submitterIsReadOnly = value.submitter.sourceType !== 'other';

  // ============================================================
  // עזרי שדה
  // ============================================================
  const updateClient = (patch: Partial<LeadSigningDetailsDto>) => onChange({ ...value, ...patch });

  const updateParent = (which: 'father' | 'mother', patch: Partial<ParentDto>) => {
    const updated: ParentDto = { ...value[which], ...patch };
    const nextValue: LeadSigningDetailsDto = { ...value, [which]: updated };

    // אם המקור של המגיש תלוי בהורה זה — נסנכרן אוטומטית
    if (value.submitter.sourceType === which) {
      nextValue.submitter = {
        ...value.submitter,
        name: updated.name,
        idNumber: updated.idNumber,
        address: updated.address,
        city: updated.city,
      };
    }
    onChange(nextValue);
  };

  const updateSpouse = (patch: Partial<SpouseDto>) => {
    const updated: SpouseDto = { ...value.spouse, ...patch };
    const nextValue: LeadSigningDetailsDto = { ...value, spouse: updated };

    if (value.submitter.sourceType === 'spouse') {
      nextValue.submitter = {
        ...value.submitter,
        name: updated.name,
        idNumber: updated.idNumber,
        address: updated.address,
        city: updated.city,
      };
    }
    onChange(nextValue);
  };

  const updateSubmitter = (patch: Partial<SubmitterDto>) =>
    onChange({ ...value, submitter: { ...value.submitter, ...patch } });

  const validateIdOnBlur = (errorKey: string, idValue: string | undefined, foreign: boolean) => {
    setError(errorKey, validateIdField(idValue, foreign));
  };

  return (
    <>
      {/* ============================================================
          בלוק 1 — פרטי לקוח (מוסתר במסכי לקוח שמציגים את הנתונים האלו במקום אחר)
          ============================================================ */}
      {!hideClientSection && <>
      <SectionHeader>פרטי לקוח</SectionHeader>
      <FormGrid>
        <FormField data-error-key="clientAddress">
          <RequiredLabel>כתובת</RequiredLabel>
          <Input
            type="text"
            value={value.clientAddress || ''}
            onChange={e => { updateClient({ clientAddress: e.target.value }); clearError('clientAddress'); }}
            hasError={!!errors.clientAddress}
            placeholder="רחוב ומספר בית"
            disabled={disabled}
          />
          {errors.clientAddress && <ErrorText>{errors.clientAddress}</ErrorText>}
        </FormField>

        <FormField data-error-key="clientCity">
          <RequiredLabel>עיר</RequiredLabel>
          <Input
            type="text"
            value={value.clientCity || ''}
            onChange={e => { updateClient({ clientCity: e.target.value }); clearError('clientCity'); }}
            hasError={!!errors.clientCity}
            placeholder="עיר מגורים"
            disabled={disabled}
          />
          {errors.clientCity && <ErrorText>{errors.clientCity}</ErrorText>}
        </FormField>

        <FormField data-error-key="clientIdNumber">
          <RequiredLabel>תעודת זהות</RequiredLabel>
          <IdFieldWrapper>
            <InputWithCheckbox>
              <Input
                type="text"
                value={value.clientIdNumber || ''}
                onChange={e => { updateClient({ clientIdNumber: e.target.value }); clearError('clientIdNumber'); }}
                onBlur={() => validateIdOnBlur('clientIdNumber', value.clientIdNumber, value.isClientForeignResident)}
                hasError={!!errors.clientIdNumber}
                placeholder="123456789"
                maxLength={9}
                disabled={disabled}
                style={{ direction: 'ltr', textAlign: 'center', flex: 1 }}
              />
              <CheckboxLabel>
                <Checkbox
                  checked={value.isClientForeignResident}
                  onChange={e => {
                    const foreign = e.target.checked;
                    updateClient({ isClientForeignResident: foreign });
                    setError('clientIdNumber', validateIdField(value.clientIdNumber, foreign));
                  }}
                  disabled={disabled}
                />
                תושב חו"ל
              </CheckboxLabel>
            </InputWithCheckbox>
          </IdFieldWrapper>
          {errors.clientIdNumber && <ErrorText>{errors.clientIdNumber}</ErrorText>}
          {value.clientIdNumber && (value.isClientForeignResident || validateIsraeliId(value.clientIdNumber)) && !errors.clientIdNumber && (
            <SuccessText>✓ {value.isClientForeignResident ? 'תושב חו"ל' : 'ת.ז. תקין'}</SuccessText>
          )}
        </FormField>

        <FormField>
          <Label>תאריך לידה</Label>
          <Input
            type="date"
            value={value.clientBirthDate ? value.clientBirthDate.substring(0, 10) : ''}
            onChange={e => updateClient({ clientBirthDate: e.target.value || undefined })}
            disabled={disabled}
            max={new Date().toISOString().substring(0, 10)}
          />
        </FormField>
      </FormGrid>
      </>}

      {/* ============================================================
          טוגל — מבוגר / ילד / אחר
          ============================================================ */}
      <SectionHeader>סוג מוטב</SectionHeader>
      <ToggleGroup>
        <ToggleButton
          type="button"
          $active={value.beneficiaryType === 'adult'}
          onClick={() => handleBeneficiaryTypeChange('adult')}
          disabled={disabled}
        >
          מבוגר
        </ToggleButton>
        <ToggleButton
          type="button"
          $active={value.beneficiaryType === 'child'}
          onClick={() => handleBeneficiaryTypeChange('child')}
          disabled={disabled}
        >
          ילד
        </ToggleButton>
        <ToggleButton
          type="button"
          $active={value.beneficiaryType === 'other'}
          onClick={() => handleBeneficiaryTypeChange('other')}
          disabled={disabled}
        >
          אחר
        </ToggleButton>
      </ToggleGroup>

      {/* ============================================================
          בלוק 2 — לפי הטוגל
          ============================================================ */}
      {value.beneficiaryType === 'child' && (
        <>
          <SectionHeader>פרטי האב</SectionHeader>
          <ParentForm
            value={value.father}
            errors={errors}
            errorPrefix="father."
            namePlaceholder="שם מלא של האב"
            disabled={disabled}
            onChange={p => updateParent('father', p)}
            onValidateId={(idVal, foreign) => validateIdOnBlur('father.idNumber', idVal, foreign)}
          />

          <SectionHeader>פרטי האם</SectionHeader>
          <ParentForm
            value={value.mother}
            errors={errors}
            errorPrefix="mother."
            namePlaceholder="שם מלא של האם"
            disabled={disabled}
            onChange={p => updateParent('mother', p)}
            onValidateId={(idVal, foreign) => validateIdOnBlur('mother.idNumber', idVal, foreign)}
          />
        </>
      )}

      {value.beneficiaryType === 'adult' && (
        <>
          <SectionHeader>פרטי בן/בת זוג</SectionHeader>
          <FormGrid>
            <FormField>
              <Label>שם</Label>
              <Input
                type="text"
                value={value.spouse.name || ''}
                onChange={e => updateSpouse({ name: e.target.value })}
                placeholder="שם מלא"
                disabled={disabled}
              />
            </FormField>

            <FormField data-error-key="spouse.idNumber">
              <Label>תעודת זהות</Label>
              <IdFieldWrapper>
                <InputWithCheckbox>
                  <Input
                    type="text"
                    value={value.spouse.idNumber || ''}
                    onChange={e => { updateSpouse({ idNumber: e.target.value }); clearError('spouse.idNumber'); }}
                    onBlur={() => validateIdOnBlur('spouse.idNumber', value.spouse.idNumber, value.spouse.isForeignResident)}
                    hasError={!!errors['spouse.idNumber']}
                    placeholder="123456789"
                    maxLength={9}
                    disabled={disabled}
                    style={{ direction: 'ltr', textAlign: 'center', flex: 1 }}
                  />
                  <CheckboxLabel>
                    <Checkbox
                      checked={value.spouse.isForeignResident}
                      onChange={e => {
                        const foreign = e.target.checked;
                        updateSpouse({ isForeignResident: foreign });
                        setError('spouse.idNumber', validateIdField(value.spouse.idNumber, foreign));
                      }}
                      disabled={disabled}
                    />
                    תושב חו"ל
                  </CheckboxLabel>
                </InputWithCheckbox>
              </IdFieldWrapper>
              {errors['spouse.idNumber'] && <ErrorText>{errors['spouse.idNumber']}</ErrorText>}
            </FormField>

            <FormField>
              <Label>כתובת</Label>
              <Input
                type="text"
                value={value.spouse.address || ''}
                onChange={e => updateSpouse({ address: e.target.value })}
                placeholder="רחוב ומספר בית"
                disabled={disabled}
              />
            </FormField>

            <FormField>
              <Label>עיר</Label>
              <Input
                type="text"
                value={value.spouse.city || ''}
                onChange={e => updateSpouse({ city: e.target.value })}
                placeholder="עיר מגורים"
                disabled={disabled}
              />
            </FormField>

            <FormField>
              <Label>תאריך לידה</Label>
              <Input
                type="date"
                value={value.spouse.birthDate ? value.spouse.birthDate.substring(0, 10) : ''}
                onChange={e => updateSpouse({ birthDate: e.target.value || undefined })}
                disabled={disabled}
                max={new Date().toISOString().substring(0, 10)}
              />
            </FormField>
          </FormGrid>
        </>
      )}

      {/* ============================================================
          בלוק 3 — מגיש הבקשה
          ============================================================ */}
      <SectionHeader>מגיש הבקשה</SectionHeader>
      <Label style={{ marginBottom: 8 }}>מקור פרטי המגיש</Label>
      <ChipRow>
        {submitterOptions.map(s => (
          <Chip
            key={s}
            type="button"
            $active={value.submitter.sourceType === s}
            onClick={() => handleSubmitterSourceChange(s)}
            disabled={disabled}
          >
            {submitterSourceLabel(s)}
          </Chip>
        ))}
      </ChipRow>

      <FormGrid>
        <FormField data-error-key="submitter.name">
          <RequiredLabel>שם המגיש</RequiredLabel>
          <Input
            type="text"
            value={value.submitter.name || ''}
            onChange={e => { updateSubmitter({ name: e.target.value }); clearError('submitter.name'); }}
            hasError={!!errors['submitter.name']}
            placeholder="שם מלא"
            disabled={disabled}
            readOnly={submitterIsReadOnly}
          />
          {errors['submitter.name'] && <ErrorText>{errors['submitter.name']}</ErrorText>}
        </FormField>

        <FormField data-error-key="submitter.idNumber">
          <RequiredLabel>תעודת זהות</RequiredLabel>
          <Input
            type="text"
            value={value.submitter.idNumber || ''}
            onChange={e => { updateSubmitter({ idNumber: e.target.value }); clearError('submitter.idNumber'); }}
            onBlur={() => {
              if (!submitterIsReadOnly) {
                validateIdOnBlur('submitter.idNumber', value.submitter.idNumber, false);
              }
            }}
            hasError={!!errors['submitter.idNumber']}
            placeholder="123456789"
            maxLength={9}
            disabled={disabled}
            readOnly={submitterIsReadOnly}
            style={{ direction: 'ltr', textAlign: 'center' }}
          />
          {errors['submitter.idNumber'] && <ErrorText>{errors['submitter.idNumber']}</ErrorText>}
        </FormField>

        <FormField>
          <Label>כתובת</Label>
          <Input
            type="text"
            value={value.submitter.address || ''}
            onChange={e => updateSubmitter({ address: e.target.value })}
            placeholder="רחוב ומספר בית"
            disabled={disabled}
            readOnly={submitterIsReadOnly}
          />
        </FormField>

        <FormField>
          <Label>עיר</Label>
          <Input
            type="text"
            value={value.submitter.city || ''}
            onChange={e => updateSubmitter({ city: e.target.value })}
            placeholder="עיר מגורים"
            disabled={disabled}
            readOnly={submitterIsReadOnly}
          />
        </FormField>

        <FormField data-error-key="submitter.relationship">
          <RequiredLabel>קרבת משפחה למבוטח</RequiredLabel>
          <select
            value={value.submitter.relationship || ''}
            onChange={e => { updateSubmitter({ relationship: e.target.value }); clearError('submitter.relationship'); }}
            disabled={disabled}
            style={{
              padding: '10px 12px',
              border: `1px solid ${errors['submitter.relationship'] ? 'var(--error-color)' : 'var(--border-color)'}`,
              borderRadius: 'var(--border-radius-sm)',
              fontSize: 14,
              background: 'white',
            }}
          >
            <option value="">בחר קרבת משפחה</option>
            {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {errors['submitter.relationship'] && <ErrorText>{errors['submitter.relationship']}</ErrorText>}
        </FormField>
      </FormGrid>
    </>
  );
};

interface ParentFormProps {
  value: ParentDto;
  errors: Record<string, string>;
  errorPrefix: 'father.' | 'mother.';
  namePlaceholder: string;
  disabled?: boolean;
  onChange: (patch: Partial<ParentDto>) => void;
  onValidateId: (idValue: string | undefined, foreign: boolean) => void;
}

const ParentForm: React.FC<ParentFormProps> = ({ value, errors, errorPrefix, namePlaceholder, disabled, onChange, onValidateId }) => (
  <FormGrid>
    <FormField data-error-key={errorPrefix + 'name'}>
      <RequiredLabel>שם</RequiredLabel>
      <Input
        type="text"
        value={value.name || ''}
        onChange={e => onChange({ name: e.target.value })}
        hasError={!!errors[errorPrefix + 'name']}
        placeholder={namePlaceholder}
        disabled={disabled}
      />
      {errors[errorPrefix + 'name'] && <ErrorText>{errors[errorPrefix + 'name']}</ErrorText>}
    </FormField>

    <FormField data-error-key={errorPrefix + 'idNumber'}>
      <RequiredLabel>תעודת זהות</RequiredLabel>
      <IdFieldWrapper>
        <InputWithCheckbox>
          <Input
            type="text"
            value={value.idNumber || ''}
            onChange={e => onChange({ idNumber: e.target.value })}
            onBlur={() => onValidateId(value.idNumber, value.isForeignResident)}
            hasError={!!errors[errorPrefix + 'idNumber']}
            placeholder="123456789"
            maxLength={9}
            disabled={disabled}
            style={{ direction: 'ltr', textAlign: 'center', flex: 1 }}
          />
          <CheckboxLabel>
            <Checkbox
              checked={value.isForeignResident}
              onChange={e => {
                const foreign = e.target.checked;
                onChange({ isForeignResident: foreign });
                onValidateId(value.idNumber, foreign);
              }}
              disabled={disabled}
            />
            תושב חו"ל
          </CheckboxLabel>
        </InputWithCheckbox>
      </IdFieldWrapper>
      {errors[errorPrefix + 'idNumber'] && <ErrorText>{errors[errorPrefix + 'idNumber']}</ErrorText>}
    </FormField>

    <FormField data-error-key={errorPrefix + 'address'}>
      <RequiredLabel>כתובת</RequiredLabel>
      <Input
        type="text"
        value={value.address || ''}
        onChange={e => onChange({ address: e.target.value })}
        hasError={!!errors[errorPrefix + 'address']}
        placeholder="רחוב ומספר בית"
        disabled={disabled}
      />
      {errors[errorPrefix + 'address'] && <ErrorText>{errors[errorPrefix + 'address']}</ErrorText>}
    </FormField>

    <FormField data-error-key={errorPrefix + 'city'}>
      <RequiredLabel>עיר</RequiredLabel>
      <Input
        type="text"
        value={value.city || ''}
        onChange={e => onChange({ city: e.target.value })}
        hasError={!!errors[errorPrefix + 'city']}
        placeholder="עיר מגורים"
        disabled={disabled}
      />
      {errors[errorPrefix + 'city'] && <ErrorText>{errors[errorPrefix + 'city']}</ErrorText>}
    </FormField>

    <FormField>
      <Label>תאריך לידה</Label>
      <Input
        type="date"
        value={value.birthDate ? value.birthDate.substring(0, 10) : ''}
        onChange={e => onChange({ birthDate: e.target.value || undefined })}
        disabled={disabled}
        max={new Date().toISOString().substring(0, 10)}
      />
    </FormField>
  </FormGrid>
);

export default LeadSigningDetails;
